import { useCallback, useMemo, useState } from "react"

import { useMutation } from "@tanstack/react-query"
import moment from "moment"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useParams } from "react-router-dom"
import { Button, Checkbox, Divider, Dropdown, Form, Label, Message, Modal, Segment } from "semantic-ui-react"
import { asMutation } from "simplydo/core/api"
import { RichText } from "simplydo/react/richtext"
import styled from "styled-components"
import { v4 as uuidv4 } from "uuid"

import actions from "actions"
import api from "api"
import AnnouncementModal from "components/lib/AnnouncementManager/AnnouncementModal"
import { challengeStandardConditions } from "components/lib/AnnouncementManager/conditions"
import EmailPreviewModal from "components/lib/AnnouncementManager/EmailPreviewModal"
import { UserChip } from "components/lib/Chips"
import { UserChooser } from "components/lib/Choosers"
import { DateTimeInput } from "components/lib/DateInputs"
import TimezoneExplainer from "components/lib/TimezoneExplainer"
import { PlaceholderItem, PlaceholderStack, TooltipButton } from "components/lib/UI"
import { useAppSelector } from "store"
import util from "utils/utils"

import Calendar, { type CellAction } from "./Calendar"
import GeneratePlanModal from "./GeneratePlanModal"

const SubmissionInputDiv = styled.div`
  display: ${({ theme }) => (theme.sizes.isMobile ? "block" : "flex")};
  flex-direction: row;
  align-items: center;
`

const ColorPickerField = styled(Form.Field)`
  .pcr-button {
    width: 38px;
    height: 38px;
  }
`

const Planner = ({ challenge, calendar, isLoading, setCalendar, getCalendar }) => {
  const [editingEvent, setEditingEvent] = useState(null)
  const [eventFilter, setEventFilter] = useState("all")
  const [generatePlanModalOpen, setGeneratePlanModalOpen] = useState(false)
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false)

  const params = useParams()
  const { t } = useTranslation()

  const user = useAppSelector((state) => state.user)
  const canPublishChallenges = useMemo(
    () => util.hasPermission(user, "org.publishChallenges", user?.ownerOrganisation?._id),
    [user],
  )
  const canManageChallenge = useMemo(() => util.canManageChallenge(user, challenge), [user, challenge])

  const challengeId = params.id

  const setEventContent = useCallback((contentOrCallback) => {
    if (typeof contentOrCallback === "function") {
      setEditingEvent((prev) => ({
        ...prev,
        eventContent: contentOrCallback(prev.eventContent),
      }))
    } else {
      setEditingEvent((prev) => ({ ...prev, eventContent: contentOrCallback }))
    }
  }, [])

  const createEvent = useCallback(
    (event) => {
      api.challenges.createCalendarEvent(
        challengeId,
        { ...event, id: uuidv4() },
        ({ events }) => {
          setEditingEvent(null)
          setCalendar((prev) => ({ ...prev, events }))
        },
        (error) => {
          toast.error(error.message)
        },
      )
    },
    [challengeId, setCalendar],
  )

  const updateEvent = useCallback(
    (event) => {
      if (event.from) {
        event.from = moment(event.from).toDate()
      }
      if (event.to) {
        event.to = moment(event.to).toDate()
      }
      if (event.type !== "event") {
        // Remove the to key from creating event
        event.to = null
      }

      api.challenges.updateCalendarEvent(
        challengeId,
        event.id,
        event,
        ({ events }) => {
          setEditingEvent(null)
          setCalendar((prev) => ({ ...prev, events }))
        },
        (error) => {
          toast.error(error.message)
        },
      )
    },
    [challengeId, setCalendar],
  )

  const handleClickCreateEvent = useCallback(() => {
    if (editingEvent?.id) {
      updateEvent(editingEvent)
    } else {
      createEvent(editingEvent)
    }
  }, [editingEvent, createEvent, updateEvent])

  const updateEventField = useCallback((field, value) => {
    setEditingEvent((prev) => ({ ...prev, [field]: value }))
  }, [])

  const updateRichtext = useCallback(
    (value, _text, fieldName) => {
      updateEventField(fieldName, value)
    },
    [updateEventField],
  )

  const deleteAnnouncement = useCallback(
    (announcementId) => {
      api.announcements.delete(
        "challenge",
        challengeId,
        announcementId,
        () => {
          getCalendar()
        },
        (err) => {
          toast.error(err.message)
        },
      )
    },
    [challengeId, getCalendar],
  )

  const updateOpenWithComms = useCallback(
    (openWithComms) => {
      api.challenges.update(
        challengeId,
        { openWithComms: openWithComms as boolean },
        () => {
          api.queryClient.setQueryData(["challenges", challengeId], (prevChallenge: any) => ({
            ...(prevChallenge || {}),
            openWithComms: openWithComms,
          }))
        },
        () => {
          toast.error("Failed to save default preferences")
        },
      )
    },
    [challengeId],
  )

  const updateChallenge = useCallback(
    (update) => {
      api.challenges.update(
        challengeId,
        update,
        () => {
          api.queryClient.setQueryData(["challenges", challengeId], (prevChallenge: any) => ({
            ...(prevChallenge || {}),
            ...update,
          }))
          getCalendar()
        },
        () => {
          toast.error("Failed to save default preferences")
        },
      )
    },
    [challengeId, getCalendar],
  )

  const setNewOpenDate = useCallback(
    (enable, openDate) => {
      updateChallenge({ openDate: enable ? moment(openDate).toDate() : null })
    },
    [updateChallenge],
  )

  const setNewDeadline = useCallback(
    (enable, deadline) => {
      updateChallenge({ deadline: enable ? moment(deadline).toDate() : null })
    },
    [updateChallenge],
  )

  const saveTemplate = useCallback(
    (update) => {
      api.challenges.updateTemplate(
        challenge._id,
        update,
        () => {
          toast.success("Assessment deadline updated")
          getCalendar()
        },
        (err) => {
          toast.error(err.message)
        },
      )
    },
    [challenge, getCalendar],
  )

  const setNewAssessmentDeadline = useCallback(
    (enable, deadline) => {
      saveTemplate({
        assessmentDeadline: enable ? moment(deadline).toDate() : null,
      })
    },
    [saveTemplate],
  )

  const deleteEvent = useCallback(
    (eventId, eventType) => {
      util
        .confirm("Delete event", "Are you sure you want to delete this event?")
        .then(() => {
          if (eventType === "announcement") {
            deleteAnnouncement(eventId)
          } else if (eventType === "open") {
            setNewOpenDate(false, null)
          } else if (eventType === "deadline") {
            setNewDeadline(false, null)
          } else if (eventType === "assessmentDeadline") {
            setNewAssessmentDeadline(false, null)
          } else {
            api.challenges.deleteCalendarEvent(
              challengeId,
              eventId,
              ({ events }) => {
                setCalendar((prev) => ({ ...prev, events }))
              },
              (error) => {
                toast.error(error.message)
              },
            )
          }
        })
        .catch(() => {})
    },
    [challengeId, deleteAnnouncement, setNewDeadline, setNewOpenDate, setCalendar, setNewAssessmentDeadline],
  )

  const cellActions = useMemo(() => {
    const actions: CellAction[] = [
      {
        name: "Schedule an announcement",
        onlyVisibleInRange: true,
        action: (dayDate) => ({
          type: "announcement",
          from: dayDate,
          eventContent: {
            sendAt: moment(dayDate).hour(9).minute(0).toISOString(),
          },
        }),
      },
    ]

    if (challenge?.stage !== "published") {
      actions.push({
        name: "Set open date",
        action: (dayDate) => ({
          type: "open",
          from: moment(dayDate).set({ hour: 9, minute: 0, second: 0, millisecond: 0 }).toDate().toISOString(),
        }),
      })
    }

    actions.push({
      name: "Set deadline",
      action: (dayDate) => ({
        type: "deadline",
        from: moment(dayDate).set({ hour: 9, minute: 0, second: 0, millisecond: 0 }).toDate().toISOString(),
      }),
      onlyVisibleInRange: true,
    })

    return actions
  }, [challenge])

  const from = useMemo(() => (calendar?.start ? new Date(calendar.start) : new Date()), [calendar])
  const nextMonth = useMemo(() => new Date(from), [from])
  nextMonth.setMonth(nextMonth.getMonth() + 1)
  const to = useMemo(() => (calendar?.end ? new Date(calendar.end) : nextMonth), [calendar, nextMonth])

  const handleCloseModal = () => {
    setEditingEvent(null)
  }

  const openDateIssue = useMemo(() => {
    return challenge?.openDate < calendar?.start || challenge?.openDate > calendar?.end
  }, [challenge, calendar?.start, calendar?.end])

  const closeDateIssue = useMemo(() => {
    return challenge?.deadline < calendar?.start || challenge?.deadline > calendar?.end
  }, [challenge, calendar?.start, calendar?.end])
  const filteredEvents = (calendar?.events || []).filter((event) => {
    // Do not filter out background events for now as they should probably always display
    if (event.eventType === "background") {
      return true
    }
    if (eventFilter === "member") {
      if (!event.members) {
        return false
      }
      return event.members.includes(user?._id)
    }
    if (eventFilter !== "all" && event.plan !== eventFilter) {
      return false
    }
    return true
  })

  const deletePlanMutation = useMutation({
    mutationKey: ["challenges", challengeId],
    mutationFn: asMutation(api.challenges.deletePlan),
    onSuccess: () => {
      toast.success("Plan deleted successfully")
      setEventFilter("all")
      getCalendar()
    },
  })

  const deletePlan = useCallback(
    (plan) => {
      util
        .confirm(
          `Delete ${plan?.name}`,
          "Are you sure you want to delete this plan? This will remove all events associated with this plan from the calendar.",
        )
        .then(() => {
          deletePlanMutation.mutate({
            params: [challengeId, plan.id],
          })
        })
    },
    [deletePlanMutation, challengeId],
  )

  return (
    <div>
      {calendar && generatePlanModalOpen && (
        <GeneratePlanModal
          generatePlanModalOpen={generatePlanModalOpen}
          onClose={() => setGeneratePlanModalOpen(false)}
          challengeId={challengeId}
          calendar={calendar}
          getCalendar={getCalendar}
        />
      )}
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        // Backwards compatibility for old event types, only using "event" now
        open={editingEvent?.type === "event" || editingEvent?.type === "meeting" || editingEvent?.type === "reminder"}
      >
        <Modal.Header>Event</Modal.Header>
        <Modal.Content scrolling>
          <Form>
            <Form.Group>
              <Form.Field>
                <label htmlFor="eventName">Name:</label>
                <input
                  id="eventName"
                  placeholder="Event name"
                  style={{ minWidth: 300 }}
                  value={editingEvent?.title}
                  onChange={(e) => setEditingEvent((prev) => ({ ...prev, title: e.target.value }))}
                />
                {(editingEvent?.title || "").length > 100 && (
                  <span
                    style={{
                      color: "red",
                    }}
                  >
                    We recommend keeping the title under 100 characters.
                  </span>
                )}
              </Form.Field>
              <ColorPickerField>
                <label htmlFor="eventColor">Color:</label>
                {[
                  "#3f88d2",
                  "#64bcd8",
                  "#f55b48",
                  "#f89c74",
                  "#f6cf71",
                  "#bdcc72",
                  "#7bba51",
                  "#fe88b1",
                  "#dcb0f2",
                  "#b18eef",
                  "#928f8f",
                ].map((color) => {
                  return (
                    <Label
                      size="large"
                      style={{
                        background: color,
                        border: editingEvent?.color === color ? "3px solid #f1f1f1bb" : "none",
                        outline: "0.5px solid #9a9a9a",
                        cursor: "pointer",
                      }}
                      circular
                      key={color}
                      onClick={() => setEditingEvent((prev) => ({ ...prev, color }))}
                    />
                  )
                })}
              </ColorPickerField>
            </Form.Group>
            <Form.Field>
              <label htmlFor="eventDescription">Description:</label>
              <RichText
                value={editingEvent?.description || ""}
                emptyReturnValue={""}
                placeholder="Describe this event..."
                onChange={updateRichtext}
                extraData="description"
                forType="challenge"
                forId={challengeId}
              />
            </Form.Field>
            <Form.Group>
              <Form.Field>
                <label htmlFor="eventStart">Start:</label>
                <DateTimeInput
                  disableMinute={true}
                  minDate={moment(from.setHours(0, 0, 0, 0)).toDate()}
                  maxDate={moment(to.setHours(23, 59, 59, 999)).toDate()}
                  dateTimeFormat="YYYY-MM-DD HH:mm"
                  value={moment(editingEvent?.from).toDate()}
                  onChange={(_e, { value }) =>
                    setEditingEvent((prev) => ({ ...prev, from: moment(value, "YYYY-MM-DD HH:mm").toDate() }))
                  }
                />
              </Form.Field>

              <Form.Field>
                <label htmlFor="eventEnd">End:</label>
                <DateTimeInput
                  minDate={moment(editingEvent?.from).toDate()}
                  maxDate={moment(to.setHours(23, 59, 59, 999)).toDate()}
                  disableMinute={true}
                  dateTimeFormat="DD-MM-YYYY HH:mm"
                  value={moment(editingEvent?.to || editingEvent?.from).toDate()}
                  onChange={(_e, { value }) => {
                    setEditingEvent((prev) => ({ ...prev, to: moment(value, "DD-MM-YYYY HH:mm").toDate() }))
                  }}
                />
              </Form.Field>
            </Form.Group>
            <Divider hidden />
            <Form.Field>
              <label htmlFor="eventMembers">Members:</label>
              {editingEvent?.ownerMembers?.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap" }}>
                  {editingEvent.ownerMembers.map((member) => (
                    <div key={member._id} style={{ marginRight: 20, minWidth: "fit-content", marginTop: 10 }}>
                      <UserChip user={member} compact />
                      <Button
                        icon="trash"
                        size="mini"
                        compact
                        style={{ marginLeft: -2 }}
                        basic
                        onClick={() =>
                          setEditingEvent((prev) => ({
                            ...prev,
                            members: prev.members.filter((m) => m !== member._id),
                            ownerMembers: prev.ownerMembers.filter((m) => m._id !== member._id),
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>
              ) : null}
            </Form.Field>
            <UserChooser
              trigger={<Button icon="user plus" size="tiny" content="Add members" />}
              enabledFeatures={{ search: true }}
              selectedUsers={editingEvent?.ownerMembers || []}
              onComplete={(members) =>
                setEditingEvent((prev) => ({ ...prev, members: members.map((m) => m._id), ownerMembers: members }))
              }
              confirm="Save members"
              searchFunction={(search, success, error) => {
                api.search.omni(
                  { query: search, include: "users" },
                  ({ users }) => {
                    success(users)
                  },
                  error,
                )
              }}
            />
            <Divider hidden />
            <Form.Group>
              <Form.Field>
                <label htmlFor="eventNotifications">Notifications:</label>
                {editingEvent?.notifications?.map((notification, index) => (
                  <div
                    key={notification._id}
                    style={{ display: "flex", alignItems: "center", marginBottom: 5, gap: 8, marginTop: 10 }}
                  >
                    <Form.Input
                      type="number"
                      value={notification.value}
                      fluid
                      min={notification.unit === "minutes" ? 10 : 0}
                      style={{ minWidth: 80 }}
                      onChange={(_e, data) => {
                        setEditingEvent((prev) => ({
                          ...prev,
                          notifications: prev.notifications.map((n, i) => {
                            if (i === index) {
                              return {
                                ...n,
                                value: parseInt(data.value, 10),
                              }
                            }
                            return n
                          }),
                        }))
                      }}
                    />
                    <Dropdown
                      selection
                      options={[
                        {
                          text: "minutes",
                          value: "minutes",
                        },
                        {
                          text: "hours",
                          value: "hours",
                        },
                        {
                          text: "days",
                          value: "days",
                        },
                      ]}
                      value={notification.unit || "minutes"}
                      onChange={(_e, data) => {
                        setEditingEvent((prev) => ({
                          ...prev,
                          notifications: prev.notifications.map((n, i) => {
                            if (i === index) {
                              return {
                                ...n,
                                unit: data.value,
                              }
                            }
                            return n
                          }),
                        }))
                      }}
                    />
                    <Button
                      icon="trash"
                      basic
                      onClick={() =>
                        setEditingEvent((prev) => ({
                          ...prev,
                          notifications: prev.notifications.filter((_n, i) => i !== index),
                        }))
                      }
                    />
                  </div>
                ))}
                <Button
                  icon="add"
                  size="tiny"
                  content="Add notification"
                  style={{ marginTop: "8px" }}
                  onClick={() =>
                    setEditingEvent((prev) => ({
                      ...prev,
                      notifications: [...(prev.notifications || []), { value: 10, unit: "minutes" }],
                    }))
                  }
                />
              </Form.Field>
            </Form.Group>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => handleCloseModal()}>Cancel</Button>
          <TooltipButton
            primary
            onClick={() => handleClickCreateEvent()}
            disabled={moment(editingEvent?.from) > moment(editingEvent?.to)}
            tooltip={
              moment(editingEvent?.from) > moment(editingEvent?.to) ? "Event end date must be after start date" : null
            }
          >
            Save
          </TooltipButton>
        </Modal.Actions>
      </Modal>
      <Modal mountNode={document.getElementById("semantic-modal-mount-node")} open={editingEvent?.type === "open"}>
        <Modal.Header>{t("common:capitalise", { key: "generic.challenge" })} open date</Modal.Header>
        <Modal.Content>
          <p>
            Set an open date for your {t("generic.challenge")}. If an open date is set, the {t("generic.challenge")}{" "}
            will automatically open at this point.
          </p>
          <TimezoneExplainer style={{ marginBottom: 10 }} />
          <SubmissionInputDiv>
            <Checkbox
              checked={!!challenge?.openDate}
              label={`There is an open date for this ${t("generic.challenge")} of`}
              onChange={() => setNewOpenDate(!challenge?.openDate, editingEvent?.from)}
              disabled={!canPublishChallenges}
              data-tooltip={
                !canPublishChallenges
                  ? `Only users with the "Publish ${t("generic.challenges")}" permission can open ${t("generic.challenges")}.`
                  : null
              }
            />
            {canPublishChallenges ? (
              <DateTimeInput
                disableMinute={true}
                value={moment(editingEvent?.from).toDate()}
                minDate={moment(new Date().setHours(0, 0, 0, 0)).toDate()}
                dateTimeFormat="YYYY-MM-DD HH:mm"
                iconPosition="left"
                onClear={() => setEditingEvent((prev) => ({ ...prev, from: moment("YYYY-MM-DD HH:mm") }))}
                style={{ marginLeft: 10, marginRight: 10 }}
                onChange={(_e, { value }) => {
                  setEditingEvent((prev) => ({ ...prev, from: moment(value, "YYYY-MM-DD HH:mm") }))
                  challenge?.openDate && setNewOpenDate(!!challenge?.openDate, moment(value, "YYYY-MM-DD HH:mm"))
                }}
              />
            ) : (
              <Label style={{ marginLeft: 7 }} content={moment(challenge?.openDate).format("YYYY-MM-DD HH:mm")} />
            )}
          </SubmissionInputDiv>
          <Checkbox
            style={{ marginTop: 10 }}
            checked={!!challenge?.openWithComms}
            label={`Send notifications and/or emails to users with interests that match this ${t("generic.challenge")} when the ${t("generic.challenge")} is opened.`}
            onChange={() => updateOpenWithComms(!challenge?.openWithComms)}
            disabled={!canPublishChallenges}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => handleCloseModal()}>Cancel</Button>
          <Button
            primary
            onClick={() => {
              setNewOpenDate(!!challenge?.openDate, moment(editingEvent?.from, "YYYY-MM-DD HH:mm"))
              handleCloseModal()
            }}
          >
            Save
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal mountNode={document.getElementById("semantic-modal-mount-node")} open={editingEvent?.type === "deadline"}>
        <Modal.Header>{t("common:capitalise", { key: "generic.challenge" })} deadline</Modal.Header>
        <Modal.Content>
          <p>
            Set a deadline for your {t("generic.challenge")}. If a deadline is set, the {t("generic.challenge")} will
            automatically close at this point.
          </p>
          <TimezoneExplainer style={{ marginBottom: 10 }} />
          <SubmissionInputDiv>
            <Checkbox
              checked={!!challenge?.deadline}
              label={`There is a deadline for this ${t("generic.challenge")} of`}
              onChange={() => setNewDeadline(!challenge?.deadline, editingEvent?.from)}
            />
            <DateTimeInput
              disableMinute={true}
              value={moment(editingEvent?.from).toDate()}
              minDate={moment(new Date().setHours(0, 0, 0, 0)).toDate()}
              dateTimeFormat="YYYY-MM-DD HH:mm"
              iconPosition="left"
              onClear={() => setEditingEvent((prev) => ({ ...prev, from: moment("YYYY-MM-DD HH:mm") }))}
              style={{ marginLeft: 10, marginRight: 10 }}
              onChange={(_e, { value }) => {
                setEditingEvent((prev) => ({ ...prev, from: moment(value, "YYYY-MM-DD HH:mm") }))
                setNewDeadline(true, moment(value, "YYYY-MM-DD HH:mm"))
              }}
            />
          </SubmissionInputDiv>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => handleCloseModal()}>Cancel</Button>
          <Button
            primary
            onClick={() => {
              setNewDeadline(challenge?.deadline, editingEvent?.from)
              handleCloseModal()
            }}
          >
            Save
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={editingEvent?.type === "assessmentDeadline"}
      >
        <Modal.Header>Assessment deadline</Modal.Header>
        <Modal.Content>
          <p>Set a deadline for assessment</p>
          <TimezoneExplainer style={{ marginBottom: 10 }} />
          <SubmissionInputDiv>
            <p style={{ margin: 0 }}>There is a deadline for this assessment of</p>
            <DateTimeInput
              disableMinute={true}
              value={moment(editingEvent?.from).toDate()}
              minDate={moment(new Date().setHours(0, 0, 0, 0)).toDate()}
              dateTimeFormat="YYYY-MM-DD HH:mm"
              iconPosition="left"
              onClear={() => setEditingEvent((prev) => ({ ...prev, from: moment("YYYY-MM-DD HH:mm") }))}
              style={{ marginLeft: 10, marginRight: 10 }}
              onChange={(_e, { value }) => {
                setEditingEvent((prev) => ({ ...prev, from: moment(value, "YYYY-MM-DD HH:mm") }))
                setNewAssessmentDeadline(true, moment(value, "YYYY-MM-DD HH:mm"))
              }}
            />
          </SubmissionInputDiv>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => handleCloseModal()}>Cancel</Button>
          <Button
            primary
            onClick={() => {
              setNewAssessmentDeadline(challenge?.ownerIdeaTemplate?.assessmentDeadline, editingEvent?.from)
              handleCloseModal()
            }}
          >
            Save
          </Button>
        </Modal.Actions>
      </Modal>
      {canManageChallenge || util.hasPermission(user, "challenge.editSettings", challengeId) ? (
        <EmailPreviewModal
          isOpen={emailPreviewOpen}
          onClose={() => setEmailPreviewOpen(false)}
          forType={"challenge"}
          forId={challengeId}
          announcement={editingEvent?.type === "announcement" && editingEvent?.eventContent}
        />
      ) : null}
      {canManageChallenge || util.hasPermission(user, "challenge.editSettings", challengeId) ? (
        <AnnouncementModal
          enabledAnnouncementTypes={["standard"]}
          forType={"challenge"}
          forId={challengeId}
          standardConditions={challengeStandardConditions}
          standardConditionsRequired
          scheduledAnnouncements={[]}
          setScheduledAnnouncements={() => {
            getCalendar()
          }}
          announcement={editingEvent?.type === "announcement" && editingEvent?.eventContent}
          setAnnouncement={setEventContent}
          deleteAnnouncement={deleteAnnouncement}
          setEmailPreviewOpen={setEmailPreviewOpen}
        />
      ) : null}
      <Segment>
        {isLoading ? (
          <PlaceholderStack gap={25}>
            <PlaceholderItem height={50} />
            <PlaceholderItem height={400} />
          </PlaceholderStack>
        ) : (
          <>
            {openDateIssue || closeDateIssue ? (
              <Message
                warning
                size="small"
                icon="warning sign"
                header="Important"
                content={`The challenge ${openDateIssue && closeDateIssue ? "open/close" : closeDateIssue ? "close" : "open"} date is currently outside of the planner date range.`}
                style={{ marginBottom: 20 }}
              />
            ) : null}

            <Form style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Form.Group>
                <Form.Dropdown
                  label="Event filter"
                  selection
                  options={[
                    { key: "all", text: "All events", value: "all" },
                    { key: "member", text: "Only events I'm a member of", value: "member" },
                    ...(calendar.plans || []).map((plan) => ({
                      key: plan.id,
                      text: plan.name,
                      value: plan.id,
                      content: (
                        <div
                          style={{
                            display: "flex",
                            lineHeight: "18px",
                            justifyContent: "space-between",
                            wordBreak: "break-word",
                            alignItems: "center",
                          }}
                        >
                          <span>{plan.name?.length > 30 ? <>{plan.name.slice(0, 30)}...</> : plan.name}</span>
                          <Button
                            icon="trash"
                            size="small"
                            style={{ marginLeft: 5 }}
                            onClick={() => deletePlan(plan)}
                          />
                        </div>
                      ),
                    })),
                  ]}
                  value={eventFilter}
                  onChange={(_, { value }) => setEventFilter(value as string)}
                />
              </Form.Group>
              {user?.ownerOrganisation?.enabledFeatures?.includes("aiPlanner") && (
                <Form.Button
                  primary
                  icon="plus"
                  content="Generate engagement plan"
                  onClick={() => setGeneratePlanModalOpen(true)}
                />
              )}
            </Form>

            <Calendar
              events={filteredEvents}
              setEvents={(events) => setCalendar((prev) => ({ ...prev, events }))}
              start={from}
              end={to}
              updateEvent={updateEvent}
              deleteEvent={
                canManageChallenge || util.hasPermission(user, "challenge.editSettings", challengeId)
                  ? deleteEvent
                  : null
              }
              leaveEvent={(eventId) => {
                util
                  .confirm("Leave event", "Are you sure you want to leave this event?")
                  .then(() => {
                    api.challenges.leaveCalendarEvent(
                      challengeId,
                      eventId,
                      ({ events }) => {
                        setCalendar((prev) => ({ ...prev, events }))
                      },
                      () => {},
                    )
                  })
                  .catch(() => {})
              }}
              setEditingEvent={setEditingEvent}
              editable={canManageChallenge || util.hasPermission(user, "challenge.editSettings", challengeId)}
              cellActions={cellActions}
            />
          </>
        )}
      </Segment>
    </div>
  )
}

export default Planner
